import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, PhoneCall, Eye, Scale, Users, ShieldCheck, BarChart3, FileCheck2, Server } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { LOGBERG_FLOW } from "../data/content";
import { useContent } from "../cms/ContentContext";

const flowIcons = [PhoneCall, Eye, Scale, Users, ShieldCheck, BarChart3];

export default function Logberg() {
  const { content } = useContent();
  const h = content.pages?.logberg || {};
  useEffect(() => {
    document.title = "L\u00f6gberg | norse3";
  }, []);

  return (
    <div>
      <PageHero
        eyebrow={h.eyebrow || "Lögberg"}
        title={h.title || "Continuous call monitoring that cuts your manual QA workload."}
        subtitle={h.subtitle || "Lögberg screens regulated calls, prioritises material risks for human review and retains linked evidence — so your team reviews what matters, not everything. Reviewer-controlled, never FCA-approved."}
      >
        <p className="text-base font-semibold text-primary mb-5">In plain English: it listens to your regulated calls so your team only reviews the ones that matter.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/book-a-demo">Discuss a Lögberg pilot <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
            <Link to="/financial-services">Financial services workflows</Link>
          </Button>
        </div>
      </PageHero>

      <section className="py-14 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The problem"
              title="You can't listen to every regulated call — but you're accountable for all of them."
              subtitle="Manual QA samples a fraction of conversations. Material risks — unsuitable advice, vulnerability, disclosure gaps — can pass unheard, with little evidence of how they were handled."
            />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="The Lögberg flow"
              title="Connect → screen → rank → route → capture → report."
              subtitle="A repeatable path from raw conversation to reviewer decision and management information."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={100}>
            {LOGBERG_FLOW.map((f, i) => {
              const Icon = flowIcons[i];
              return (
                <div key={f.step} className="relative rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <span className="absolute right-5 top-5 text-xs font-mono text-primary/50">0{i + 1}</span>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">{f.step}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <SectionHeading
                eyebrow="Reviewer-controlled"
                title="Evidence that supports oversight, not automatic sign-off."
                subtitle="Lögberg helps reviewers focus and record their decisions. It does not approve outcomes on your behalf."
              />
              <ul className="mt-8 space-y-3">
                {[
                  "Material issues ranked so reviewers focus where it counts",
                  "Cases routed to the right reviewer with full context",
                  "Decisions and rationale captured against a repeatable process",
                  "Linked evidence retained for assurance and audit",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground">
                    <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-lg border border-white/10 bg-navy-900/70 p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">Review queue</p>
                  <span className="text-xs text-muted-foreground">Illustrative</span>
                </div>
                <div className="mt-4 space-y-2.5">
                  {[
                    { id: "CASE-2048", risk: "High", tone: "text-signal-red bg-signal-red/12", label: "Potential unauthorised advice" },
                    { id: "CASE-2049", risk: "Med", tone: "text-signal-amber bg-signal-amber/12", label: "Vulnerability indicator" },
                    { id: "CASE-2050", risk: "Low", tone: "text-signal-green bg-signal-green/12", label: "Disclosure completeness" },
                  ].map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3">
                      <div>
                        <p className="text-sm text-foreground">{c.label}</p>
                        <p className="text-xs text-muted-foreground font-mono">{c.id}</p>
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c.tone}`}>{c.risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <SectionHeading eyebrow="Evidence" title="A defensible record for every case." subtitle="Reviewer decisions and rationale are captured against a repeatable process and retained as linked evidence." />
            <ul className="mt-6 space-y-2.5">
              {["Conversation reference and risk classification", "Policy or obligation the case was assessed against", "Reviewer, decision and rationale", "Timeline and management information"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground"><FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{t}</span></li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <SectionHeading eyebrow="Deployment" title="Fits your environment." subtitle="Deploy against your telephony and data requirements." />
            <div className="mt-6 grid gap-3">
              {[["Hosted or private cloud", "Chosen against sensitivity and data residency."], ["Reviewer-controlled", "Lögberg prioritises and records — people decide."], ["Integrates with your stack", "Works alongside your call and QA systems."]].map(([t, d]) => (
                <div key={t} className="rounded-xl border border-white/10 bg-navy-900/60 p-4 flex items-start gap-3">
                  <Server className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div><p className="text-sm font-semibold text-foreground">{t}</p><p className="text-sm text-muted-foreground">{d}</p></div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal><SectionHeading eyebrow="FAQ" title="Common questions." /></Reveal>
          <div className="mt-8 space-y-3">
            {[
              ["Does Lögberg approve or reject outcomes?", "No. Lögberg screens, prioritises and records — the decision stays with your reviewers. It supports oversight, it does not replace it."],
              ["Is it FCA-approved?", "No product is FCA-approved. Lögberg is designed to support alignment with your obligations; your firm remains accountable."],
              ["Does it review every call?", "It can screen all captured conversations and surface the material ones for human review, rather than sampling a small percentage."],
              ["How is evidence retained?", "As a linked, time-stamped record of the case, risk, policy, reviewer and decision — configurable to your retention requirements."],
            ].map(([q, a]) => (
              <details key={q} className="group rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground flex items-center justify-between">{q}<ArrowDown className="h-4 w-4 text-primary transition-transform group-open:rotate-180" /></summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl font-bold text-foreground">Explore a Lögberg pilot.</h2>
            <p className="mt-4 text-muted-foreground">
              We will walk through your conversations, obligations and reviewer workflow, and
              show where Lögberg fits.
            </p>
            <Button asChild size="lg" className="mt-7 bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/book-a-demo">Discuss a Lögberg pilot <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
